import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCreate, useUpdate, useNotify, useGetList } from "react-admin";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import {
  Alert,
  alpha,
  Autocomplete,
  Box,
  Button as MuiButton,
  Chip,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import Button from "@/components/ui/Button";
import { ConfirmationDialog } from "@/components/ui/ConfirmationDialog";
import {
  ButtonStyleType,
  CancelLabelType,
  ColorType,
  ConfirmLabelType,
  UIComponentSize,
} from "@/types/shared/uiComponents";
import {
  AlertType,
  ResourceName,
  hasStraightLaser,
  type Claimant,
  type ContractLaser,
  type ContractLaserRequest,
  type DateYMDString,
  type LaseredClaimantListItem,
  type SpecificBenefit,
  type ValidAny,
} from "@/types";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { useAppTheme } from "@/hooks/useAppTheme";
import { AutocompleteCodeInput } from "@/components/common/fields/AutocompleteCodeInput";
import { validateLaseredEffectiveDate } from "@/utils/validations/laseredClaimantValidation";
import { validateContractLaser } from "@/utils/validations/contractLaserValidation";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { useDeferredRecomputeRefresh, useRecomputeRefresh } from "@/resources/claimants/hooks/useRecomputeRefresh";
import { resolveConditionWindowDefaults } from "@/utils/helpers/conditionWindowDefaults";
import {
  LASER_KIND_LABELS,
  LASER_KIND_OPTIONS,
  NO_LASER_CLEAR,
  STANDING_DEDUCTIBLE_HINT,
  conditionListText,
  describeLaserClear,
  hasContractLaser,
  joinAnd,
  laserClearPlan,
  type ExistingLaser,
  type LaserKind,
} from "@/utils/helpers/laserKind";
import { putContractLaser, useContractLaser } from "../hooks/useContractLaser";
import { useLaseredConditionDrafts, writesBeforeFailure } from "../hooks/useLaseredConditionDrafts";
import { linkClaimantToPolicy, removeLaseredClaimant } from "../hooks/useLaseredConditions";
import { LaseredConditionsEditor } from "./LaseredConditionsEditor";

/** A stored `condition_based` straight laser, shown as Standard (no longer offered). */
export const LEGACY_CONDITION_BASED_NOTE =
  "Legacy condition-based laser: its deductible applies to all claims. The condition and codes it was recorded with are kept as they are.";

const NO_DECIMALS = { minimumFractionDigits: 0, maximumFractionDigits: 0 };

/**
 * One write of a save. `once` steps are not sent again on a retry after a later
 * step failed; the others are safe to repeat (the conditional-laser writes skip
 * what is already saved).
 */
interface SaveStep {
  key: string;
  label: string;
  once: boolean;
  run: () => Promise<void>;
}

/** Progress of a save that stopped part-way, for the alert and the field locks. */
interface SaveProgress {
  done: { key: string; label: string }[];
  failed: { key: string; label: string };
  todo: string[];
}

/** What a multi-write step finished before it failed, for "Not everything was saved". */
const partialLabel = (stepKey: string, n: number): string =>
  stepKey === "clearConditions"
    ? `${n} conditional laser${n === 1 ? "" : "s"} removed`
    : `${n} conditional laser change${n === 1 ? "" : "s"}`;

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

interface LaseredClaimantDialogProps {
  open: boolean;
  onClose: () => void;
  policyId: string;
  clientId?: string;
  /** Policy effective start (YYYY-MM-DD). Used to default and bound effective_date. */
  policyEffectiveStart?: string;
  /** Policy effective end (YYYY-MM-DD). Upper bound for effective_date. */
  policyEffectiveEnd?: string;
  /** Policy SpecificBenefit — used to pre-fill the contract-laser date windows. */
  specificBenefit?: SpecificBenefit;
  mode: "create" | "edit";
  existingRecord?: LaseredClaimantListItem;
  /**
   * When set in create mode, bind the new laser to this existing claimant and
   * hide the claimant picker. Used by the claimant-page "Add Laser" affordance
   * so the laser is scoped to the claimant already in view (and the selected
   * policy passed via `policyId`), not chosen from a list.
   */
  presetClaimant?: Claimant;
  onSuccess?: () => void;
  /**
   * Offer expected triggers on saved conditional lasers (#7361). The policy
   * page's Lasered Claimants tab only: lasers are managed there (PO 2026-10-07).
   */
  expectedTriggers?: boolean;
}

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

/**
 * Add or edit a lasered claimant on a policy. A lasered claimant is exactly one
 * type (PO 2026-10-07):
 * - Standard: a lasered deductible and its effective date (a straight laser).
 * - Contract: a contract laser or a full exclusion (`PUT .../contract-laser`).
 * - Conditional: one or more conditional lasers plus an optional standing
 *   deductible (a straight laser); without one the policy base applies.
 *
 * Changing the type removes the old type's data after a confirm that lists it.
 * Writes go new type first, then removals, so a failure part-way leaves the
 * claimant listed; the dialog then stays open, says what is done and what is
 * not, and a retry sends only what is left. Fields the form no longer shows
 * (incremental deductible, laser reason, a legacy condition-based laser's
 * condition) are sent back as stored.
 */
export const LaseredClaimantDialog = ({
  open,
  onClose,
  policyId,
  clientId,
  policyEffectiveStart,
  policyEffectiveEnd,
  specificBenefit,
  mode,
  existingRecord,
  presetClaimant,
  onSuccess,
  expectedTriggers,
}: LaseredClaimantDialogProps) => {
  const { theme } = useAppTheme();
  const [create] = useCreate();
  const [update] = useUpdate();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  // #5896: this dialog mounts BOTH under the claimant page (Overview tab, inside
  // RecomputeRefreshProvider — where the stat tiles read isRecomputing from the
  // context) AND on the policy page (LaseredClaimantsTab, no provider). Prefer the
  // context when present so the tiles show the "updating…" cue; otherwise fall
  // back to a standalone instance so the deferred refetch still runs. Calling both
  // hooks unconditionally satisfies the rules of hooks; the unused one is inert.
  const contextRefresh = useRecomputeRefresh();
  const standaloneRefresh = useDeferredRecomputeRefresh();
  const { triggerRecomputeRefresh } = contextRefresh.isProvided ? contextRefresh : standaloneRefresh;

  // ── Claimant autocomplete (for create mode) ──
  const { data: claimants = [] } = useGetList<Claimant>(
    ResourceName.CLAIMANTS,
    {
      filter: clientId ? { client_id_ref: clientId } : {},
      pagination: { page: 1, perPage: 500 },
      sort: { field: "claimant_name", order: "ASC" },
    },
    { enabled: open && mode === "create" && !!clientId }
  );

  // ── Controlled form state ──
  const [selectedClaimant, setSelectedClaimant] = useState<Claimant | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(true);
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const [newMemberId, setNewMemberId] = useState("");

  // Laser type. Until the user picks one, an edit follows the record (and the
  // fetched contract laser / conditional lasers) once those have loaded.
  const [kind, setKind] = useState<LaserKind | "">("");
  const [kindTouched, setKindTouched] = useState(false);
  // The type the claimant had when this opened (per create-mode claimant): a
  // save as another type removes the old one's data. Fixed once loaded, so the
  // refetches after a part-way save do not move it.
  const [origin, setOrigin] = useState<{ key: string; kind?: LaserKind } | undefined>(undefined);

  // Straight laser: the Standard lasered deductible, or the Conditional standing deductible.
  const [amtLaseredDeductible, setAmtLaseredDeductible] = useState("");
  const [effectiveDate, setEffectiveDate] = useState<string>("");

  // ── Contract-laser fields (Feature #5287) ──
  // A contract laser is an elevated specific deductible scoped to a contract
  // period (incurred + paid windows) with specific CPTs excluded. A full
  // exclusion carves the claimant out of the contract entirely — the two are
  // mutually exclusive (the toggle below enforces that on the client).
  const [isContractExcluded, setIsContractExcluded] = useState(false);
  const [contractDeductible, setContractDeductible] = useState("");
  const [contractIncurredStart, setContractIncurredStart] = useState("");
  const [contractIncurredEnd, setContractIncurredEnd] = useState("");
  const [contractPaidStart, setContractPaidStart] = useState("");
  const [contractPaidEnd, setContractPaidEnd] = useState("");
  const [contractExcludedCpts, setContractExcludedCpts] = useState<string[]>([]);
  // Contract errors show once the user has touched the section or tried to save.
  const [contractTouched, setContractTouched] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // ── Save state ──
  const [saving, setSaving] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  // Edited conditional lasers that leave their expected trigger uncovered (#7361).
  const [confirmStrandedOpen, setConfirmStrandedOpen] = useState(false);
  // The stranded-trigger warning the user confirmed, so a retry asks again only
  // when the warning changed (e.g. a window narrowed after a part-way save):
  // the SPA warning is the only guard (PO 2026-10-07; !4881 thread 30548).
  const confirmedStranded = useRef("");
  const [editConfirmOpen, setEditConfirmOpen] = useState(false);
  const [savedKind, setSavedKind] = useState<LaserKind>("standard");
  // Create mode: the claimant once created, so a retry does not create it twice.
  const [createdClaimantId, setCreatedClaimantId] = useState<string | undefined>(undefined);
  // Steps already written in this open (survive a failed save; a retry skips the `once` ones).
  const doneSteps = useRef<Map<string, string>>(new Map());
  const [progress, setProgress] = useState<SaveProgress | null>(null);

  // Existing claimant id (edit mode) — drives the contract-laser fetch.
  const existingClaimantId = mode === "edit" ? existingRecord?.id : undefined;

  // Fetch current contract-laser state for an existing claimant when the dialog opens.
  const {
    contractLaser,
    isLoading: contractLoading,
    refetch: refetchContractLaser,
  } = useContractLaser(policyId, existingClaimantId, open && mode === "edit" && !!existingClaimantId);

  // ── Conditional lasers (Feature #3502, #3511/#7344) ──
  // Rows load for a claimant already known to the dialog (edit, preset or a
  // picked existing claimant, who may have conditions without a straight laser).
  // A claimant this dialog creates is not one of them: its rows stay local
  // until written to it (a retry writes the rest to the same claimant).
  const conditionClaimantId =
    mode === "edit"
      ? existingRecord?.id
      : (presetClaimant?.id ?? (!isCreatingNew && selectedClaimant ? String(selectedClaimant.id) : undefined));
  const conditionWindowDefaults = useMemo(
    () =>
      resolveConditionWindowDefaults({
        specificBenefit,
        policyStart: policyEffectiveStart,
        policyEnd: policyEffectiveEnd,
      }),
    [specificBenefit, policyEffectiveStart, policyEffectiveEnd]
  );
  const conditionDrafts = useLaseredConditionDrafts({
    policyId,
    claimantId: conditionClaimantId ? String(conditionClaimantId) : undefined,
    defaults: conditionWindowDefaults,
    active: open,
  });

  // ── What the claimant has on this policy now ──
  const existingStraight =
    mode === "edit" && existingRecord && hasStraightLaser(existingRecord) ? existingRecord : undefined;
  const savedContract: ContractLaser | undefined =
    mode === "edit" ? (contractLaser ?? existingRecord?.contract_laser) : undefined;
  const existingContract = hasContractLaser(savedContract) ? savedContract : undefined;
  const savedConditions = conditionDrafts.savedReady
    ? conditionDrafts.saved
    : mode === "edit"
      ? (existingRecord?.lasered_conditions ?? [])
      : [];
  const existing: ExistingLaser = useMemo(
    () => ({
      straightAmount: existingStraight?.amt_lasered_deductible,
      conditions: savedConditions,
      contract: existingContract,
    }),
    [existingStraight, savedConditions, existingContract]
  );
  const existingReady = conditionDrafts.savedReady && (mode !== "edit" || !contractLoading);
  const derivedKind: LaserKind | undefined = existingContract
    ? "contract"
    : savedConditions.length > 0
      ? "conditional"
      : existingStraight
        ? "standard"
        : undefined;
  const originKey = conditionClaimantId ? String(conditionClaimantId) : "new";
  const originSettled = origin?.key === originKey;
  const originKind = originSettled ? origin.kind : derivedKind;
  // Untouched, the type is the record's: fixed once loaded (the settle effect
  // only records it, so it can never overwrite a choice the user just made).
  const selectedKind: LaserKind | "" = kindTouched ? kind : (originKind ?? "");
  const isLegacyConditionBased = existingStraight?.laser_type === "condition_based";

  useEffect(() => {
    if (!open || !existingReady || originSettled) return;
    setOrigin({ key: originKey, kind: derivedKind });
  }, [open, existingReady, originSettled, originKey, derivedKind]);

  // Progress locks: after a part-way save the type and the claimant are fixed,
  // and so is each part already written; the parts not yet written stay editable.
  const partlySaved = !!progress || !!createdClaimantId;
  const straightLocked = partlySaved && doneSteps.current.has("straight");
  const contractLocked = partlySaved && doneSteps.current.has("contract");

  // Create mode for a known claimant (preset or picked): their contract tiers on
  // this policy are on the record (#3511 AC6). A brand-new claimant has none.
  const createTargetPolicyData =
    mode === "create"
      ? (presetClaimant ?? (!isCreatingNew ? selectedClaimant : null))?.policy_data?.[policyId]
      : undefined;
  const createTargetExcluded = !!createTargetPolicyData?.is_contract_excluded;
  const conditionsDisabledReason = createTargetExcluded
    ? "This claimant is fully excluded from the contract on this policy, so conditional lasers cannot apply."
    : undefined;
  const conditionsInertWarning =
    !createTargetExcluded && createTargetPolicyData?.is_contract_lasered
      ? "This claimant has a contract laser on this policy. Conditional lasers are saved but stay inert while it is set."
      : undefined;

  // Expected triggers (#7361) save at once and publish a recompute. Not offered
  // for a claimant with a saved contract laser or exclusion (AC7): their
  // conditional lasers are inert.
  const expectedTriggerOptions = useMemo(
    () =>
      expectedTriggers
        ? {
            contractClaimant:
              !!existingContract || createTargetExcluded || !!createTargetPolicyData?.is_contract_lasered,
            onSaved: () => triggerRecomputeRefresh({ notifyUser: false }),
          }
        : undefined,
    [expectedTriggers, existingContract, createTargetExcluded, createTargetPolicyData, triggerRecomputeRefresh]
  );

  // Default the date windows from the policy's SpecificBenefit — all editable.
  // Same defaults as a conditional laser's windows: the specific benefit's
  // windows, else the policy term (#7344 order).
  const defaultContractDates = useMemo(
    () => ({
      incurredStart: conditionWindowDefaults.windows.incurred_start_date ?? "",
      incurredEnd: conditionWindowDefaults.windows.incurred_end_date ?? "",
      paidStart: conditionWindowDefaults.windows.paid_start_date ?? "",
      paidEnd: conditionWindowDefaults.windows.paid_end_date ?? "",
    }),
    [conditionWindowDefaults]
  );

  // ── Initialize form when dialog opens ──
  // Keyed on open / mode / record ids, not object identity: a refetch of the
  // claimant or the lasered-claimants list hands in new objects for the same
  // claimant, and resetting then would wipe the form and the retry guard (a
  // retry could create the claimant or laser twice).
  const initializedFor = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!open) {
      initializedFor.current = undefined;
      return;
    }
    const initKey = `${mode}:${existingRecord?.id ?? ""}:${presetClaimant?.id ?? ""}`;
    if (initializedFor.current === initKey) return;
    initializedFor.current = initKey;

    setKind("");
    setKindTouched(false);
    setOrigin(undefined);
    setSubmitAttempted(false);
    setContractTouched(false);
    setConfirmClearOpen(false);
    setConfirmStrandedOpen(false);
    confirmedStranded.current = "";
    setEditConfirmOpen(false);
    setCreatedClaimantId(undefined);
    setProgress(null);
    doneSteps.current = new Map();

    if (mode === "edit" && existingRecord) {
      const straight = hasStraightLaser(existingRecord);
      setSelectedClaimant(null);
      setIsCreatingNew(false);
      setNewFirstName("");
      setNewLastName("");
      setNewMemberId("");
      setAmtLaseredDeductible(straight ? String(existingRecord.amt_lasered_deductible ?? "") : "");
      // Legacy records created before #4535 have no effective_date. Backfill with the
      // policy start, but tell the user we did so — saving without touching the field
      // would otherwise silently mutate the record.
      if (straight && !existingRecord.effective_date && policyEffectiveStart) {
        notify(
          "This laser was missing an effective date. Defaulted to the policy start — adjust if needed before saving.",
          { type: AlertType.INFO }
        );
      }
      setEffectiveDate(existingRecord.effective_date ?? policyEffectiveStart ?? "");
      // Contract laser: seed from the embedded list-item snapshot if present; the
      // fetch effect below refines this once the dedicated endpoint responds.
      const cl = existingRecord.contract_laser;
      setIsContractExcluded(cl?.is_contract_excluded ?? false);
      setContractDeductible(cl?.amt_contract_lasered_deductible ? String(cl.amt_contract_lasered_deductible) : "");
      setContractIncurredStart(cl?.contract_incurred_start_date ?? defaultContractDates.incurredStart);
      setContractIncurredEnd(cl?.contract_incurred_end_date ?? defaultContractDates.incurredEnd);
      setContractPaidStart(cl?.contract_paid_start_date ?? defaultContractDates.paidStart);
      setContractPaidEnd(cl?.contract_paid_end_date ?? defaultContractDates.paidEnd);
      setContractExcludedCpts(cl?.excluded_procedures ?? []);
    } else {
      // presetClaimant binds create mode to a known claimant (claimant-page "Add
      // Laser"); otherwise the picker/create-new UI drives selection.
      setSelectedClaimant(presetClaimant ?? null);
      setIsCreatingNew(!presetClaimant);
      setNewFirstName("");
      setNewLastName("");
      setNewMemberId("");
      setAmtLaseredDeductible("");
      setEffectiveDate(policyEffectiveStart ?? "");
      // Contract laser: pre-fill date windows from the policy SpecificBenefit; all editable.
      setIsContractExcluded(false);
      setContractDeductible("");
      setContractIncurredStart(defaultContractDates.incurredStart);
      setContractIncurredEnd(defaultContractDates.incurredEnd);
      setContractPaidStart(defaultContractDates.paidStart);
      setContractPaidEnd(defaultContractDates.paidEnd);
      setContractExcludedCpts([]);
    }
  }, [open, mode, existingRecord, presetClaimant, policyEffectiveStart, defaultContractDates, notify]);

  // When the dedicated contract-laser endpoint responds for an existing claimant,
  // populate the contract fields (laser vs. full exclusion vs. none).
  useEffect(() => {
    if (!open || mode !== "edit" || !contractLaser) return;
    setIsContractExcluded(contractLaser.is_contract_excluded);
    setContractDeductible(
      contractLaser.amt_contract_lasered_deductible ? String(contractLaser.amt_contract_lasered_deductible) : ""
    );
    setContractIncurredStart(contractLaser.contract_incurred_start_date ?? defaultContractDates.incurredStart);
    setContractIncurredEnd(contractLaser.contract_incurred_end_date ?? defaultContractDates.incurredEnd);
    setContractPaidStart(contractLaser.contract_paid_start_date ?? defaultContractDates.paidStart);
    setContractPaidEnd(contractLaser.contract_paid_end_date ?? defaultContractDates.paidEnd);
    setContractExcludedCpts(contractLaser.excluded_procedures ?? []);
  }, [open, mode, contractLaser, defaultContractDates]);

  const claimantDisplayName = useCallback((c: Claimant) => {
    const parts = [c.claimant_last_name, c.claimant_first_name].filter(Boolean);
    const name = parts.join(", ") || c.claimant_name || "Unknown";
    return c.member_id ? `${name} (${c.member_id})` : name;
  }, []);

  // Claimants already lasered on this policy (any type we can see on the record) are not offered.
  const availableClaimants = useMemo(
    () =>
      claimants.filter((c) => {
        const pd = c.policy_data?.[policyId];
        return !pd?.is_lasered && !pd?.is_contract_lasered && !pd?.is_contract_excluded;
      }),
    [claimants, policyId]
  );

  // ── Validation ──
  const standingSet = amtLaseredDeductible.trim() !== "";
  const writesStraight = selectedKind === "standard" || (selectedKind === "conditional" && standingSet);
  const amount = parseFloat(amtLaseredDeductible);

  /**
   * Shown as typed: a non-positive amount, a date outside the policy. Blocks Save.
   * Only for an amount this save writes: a legacy $0 laser seeds "0", and Contract
   * hides the field (!4880 thread 30680).
   */
  const amountError =
    writesStraight && standingSet && !(amount > 0)
      ? selectedKind === "conditional"
        ? "The lasered specific deductible must be greater than $0, or leave it blank."
        : "The lasered deductible must be greater than $0."
      : "";
  const effectiveDateError =
    writesStraight && effectiveDate
      ? validateLaseredEffectiveDate({
          laserType: "individual",
          effectiveDate,
          policyStart: policyEffectiveStart,
          policyEnd: policyEffectiveEnd,
        })
      : "";
  const contractLaserError =
    selectedKind === "contract"
      ? validateContractLaser({
          isExcluded: isContractExcluded,
          incurredStart: contractIncurredStart,
          incurredEnd: contractIncurredEnd,
          paidStart: contractPaidStart,
          paidEnd: contractPaidEnd,
          deductible: contractDeductible,
        })
      : "";
  const showContractError = !!contractLaserError && (contractTouched || submitAttempted);
  const conditionRowError = selectedKind === "conditional" ? conditionDrafts.error : "";
  // Without the saved rows a type change could not remove them; block every save.
  const conditionLoadError = conditionDrafts.loadFailed ? conditionDrafts.error : "";

  /** Missing input: shown after a save attempt, not as the user starts typing. */
  const missingError = (() => {
    if (mode === "create" && !presetClaimant && !createdClaimantId) {
      if (isCreatingNew && (!newFirstName.trim() || !newLastName.trim()))
        return "First name and last name are required";
      if (isCreatingNew && !clientId) return "Unable to determine client for this policy";
      if (!isCreatingNew && !selectedClaimant) return "Please select a claimant";
    }
    if (!selectedKind) return "Choose a laser type.";
    if (selectedKind === "standard" && !standingSet) return "Enter the lasered specific deductible.";
    if (writesStraight && !effectiveDate) return "Enter the effective date.";
    if (selectedKind === "conditional" && conditionDrafts.drafts.length === 0) {
      return "Add at least one conditional laser.";
    }
    return "";
  })();
  const blockingError =
    conditionLoadError ||
    amountError ||
    effectiveDateError ||
    (showContractError ? contractLaserError : "") ||
    conditionRowError;

  // ── What this save removes ──
  const clearPlan = selectedKind
    ? laserClearPlan({ origin: originKind, kind: selectedKind, existing, standingSet })
    : NO_LASER_CLEAR;
  const clearText = describeLaserClear(clearPlan, existing);
  // PO 2026-10-07 (#7361): an edit that leaves an expected trigger outside the
  // new terms is allowed, after a warning (the row shows it while editing).
  const stranded = selectedKind === "conditional" ? conditionDrafts.strandedExpectedTriggers : [];
  const strandedText = stranded.map((r) => `${r.name}: ${r.warning}`).join(" ");

  /**
   * Build the PUT body for the active contract-laser mode (mutually exclusive).
   * Clearing (both flags false) is a separate step of a type change.
   */
  const buildContractLaserBody = useCallback((): ContractLaserRequest => {
    if (isContractExcluded) {
      return { is_contract_lasered: false, is_contract_excluded: true };
    }
    return {
      is_contract_lasered: true,
      is_contract_excluded: false,
      amt_contract_lasered_deductible: parseFloat(contractDeductible) || 0,
      contract_incurred_start_date: contractIncurredStart as DateYMDString,
      contract_incurred_end_date: contractIncurredEnd as DateYMDString,
      contract_paid_start_date: contractPaidStart as DateYMDString,
      contract_paid_end_date: contractPaidEnd as DateYMDString,
      excluded_procedures: contractExcludedCpts.map((s) => s.trim().toUpperCase()).filter(Boolean),
    };
  }, [
    isContractExcluded,
    contractDeductible,
    contractIncurredStart,
    contractIncurredEnd,
    contractPaidStart,
    contractPaidEnd,
    contractExcludedCpts,
  ]);

  /** Run the save: claimant (create), the new type's writes, then removals. */
  const runSave = useCallback(async () => {
    if (!selectedKind) return;
    const saveKind = selectedKind;
    const resource = `policies/${policyId}/lasered-claimants`;
    const ctx: { claimantId?: string } = {
      claimantId:
        mode === "edit"
          ? existingRecord?.id
          : (createdClaimantId ??
            (presetClaimant ? String(presetClaimant.id) : selectedClaimant ? String(selectedClaimant.id) : undefined)),
    };
    const claimantIdOrThrow = () => {
      if (!ctx.claimantId) throw new Error("Unable to determine the claimant");
      return ctx.claimantId;
    };
    const steps: SaveStep[] = [];

    if (mode === "create" && isCreatingNew && !presetClaimant && !createdClaimantId) {
      steps.push({
        key: "claimant",
        label: "the claimant",
        once: true,
        run: async () => {
          const payload: Record<string, string> = {
            claimant_first_name: newFirstName.trim(),
            claimant_last_name: newLastName.trim(),
            client_id_ref: clientId as string,
          };
          if (newMemberId.trim()) payload.member_id = newMemberId.trim();
          const created = await create(ResourceName.CLAIMANTS, { data: payload }, { returnPromise: true });
          const id = String((created as ValidAny).data?.id ?? (created as ValidAny).id);
          ctx.claimantId = id;
          setCreatedClaimantId(id);
        },
      });
    }

    const writeStraight: SaveStep = {
      key: "straight",
      label: "the lasered specific deductible",
      once: true,
      run: async () => {
        const date = effectiveDate ? { effective_date: effectiveDate } : {};
        if (existingStraight) {
          // The API's PUT treats an omitted amount or reason as empty: send the
          // stored values of what the form no longer shows (and keep a legacy
          // condition_based laser as it is).
          await update(
            resource,
            {
              id: existingStraight.id,
              data: {
                laser_type: existingStraight.laser_type === "condition_based" ? "condition_based" : "individual",
                laser_reason: existingStraight.laser_reason ?? "",
                amt_lasered_deductible: amount,
                amt_incremental_lasered_deductible: existingStraight.amt_incremental_lasered_deductible ?? 0,
                ...date,
                conditions: existingStraight.conditions ?? null,
              },
            },
            { returnPromise: true }
          );
        } else {
          await create(
            resource,
            {
              data: {
                claimant_id_ref: claimantIdOrThrow(),
                laser_type: "individual",
                amt_lasered_deductible: amount,
                amt_incremental_lasered_deductible: 0,
                ...date,
              },
            },
            { returnPromise: true }
          );
        }
      },
    };
    const writeContract: SaveStep = {
      key: "contract",
      label: isContractExcluded ? "the contract exclusion" : "the contract laser",
      once: true,
      run: async () => {
        const response = await putContractLaser(policyId, claimantIdOrThrow(), buildContractLaserBody());
        // "Already has a straight laser" is moot when this save removes it.
        if (response?.warning && !clearPlan.straight) notify(response.warning, { type: AlertType.INFO });
      },
    };
    const writeConditions: SaveStep = {
      key: "conditions",
      label: "the conditional lasers",
      once: false,
      run: async () => {
        if (!conditionDrafts.dirty) return;
        const warnings = await conditionDrafts.persist(claimantIdOrThrow());
        // "Inert under a contract laser" is moot when this save removes it.
        if (!clearPlan.contract) warnings.forEach((w) => notify(w, { type: AlertType.INFO }));
      },
    };
    const linkPolicy: SaveStep = {
      key: "link",
      label: "the link to this policy",
      once: true,
      run: () => linkClaimantToPolicy(policyId, claimantIdOrThrow()),
    };
    const clearConditions: SaveStep = {
      key: "clearConditions",
      label: "removing the conditional lasers",
      once: false,
      run: () => conditionDrafts.removeAll(claimantIdOrThrow()),
    };
    const clearStraight: SaveStep = {
      key: "clearStraight",
      label: "removing the straight laser",
      once: true,
      // Only the straight laser, and a no-op once it's gone: a retry after a
      // lost response, or a laser another user removed, can't fall through to
      // the conditional or contract laser this save wrote (!4880 thread 30532).
      run: () => removeLaseredClaimant(policyId, claimantIdOrThrow(), { straightOnly: true }),
    };
    const clearContract: SaveStep = {
      key: "clearContract",
      label: existingContract?.is_contract_excluded ? "removing the contract exclusion" : "removing the contract laser",
      once: true,
      run: async () => {
        await putContractLaser(policyId, claimantIdOrThrow(), {
          is_contract_lasered: false,
          is_contract_excluded: false,
        });
      },
    };

    // The new type first, so the claimant stays listed if a removal fails. The
    // one exception: conditional lasers cannot be added while the claimant is
    // excluded from the contract, so that exclusion goes first.
    if (saveKind === "standard") {
      steps.push(writeStraight);
      if (clearPlan.conditions) steps.push(clearConditions);
      if (clearPlan.contract) steps.push(clearContract);
    } else if (saveKind === "contract") {
      steps.push(writeContract);
      if (clearPlan.conditions) steps.push(clearConditions);
      if (clearPlan.straight) steps.push(clearStraight);
    } else {
      const exclusionFirst = clearPlan.contract && !!existingContract?.is_contract_excluded;
      if (standingSet) steps.push(writeStraight);
      // Nothing else in this save puts a new or unlinked claimant on the policy,
      // and the API refuses their conditions until it does (!4880 thread 30533).
      else if (mode === "create" && !createTargetPolicyData) steps.push(linkPolicy);
      if (exclusionFirst) steps.push(clearContract);
      steps.push(writeConditions);
      if (clearPlan.contract && !exclusionFirst) steps.push(clearContract);
      if (clearPlan.straight) steps.push(clearStraight);
    }

    setSaving(true);
    let current: SaveStep | undefined;
    try {
      for (const step of steps) {
        current = step;
        if (step.once && doneSteps.current.has(step.key)) continue;
        await step.run();
        doneSteps.current.set(step.key, step.label);
      }
    } catch (err) {
      // Server state moved if a step finished, or a multi-write step (the
      // conditional lasers) got part-way.
      if (doneSteps.current.size > 0 || (current && !current.once)) triggerRecomputeRefresh({ notifyUser: false });
      notifyError(err);
      // A multi-write step (the conditional lasers) that got part-way counts as
      // progress too, even when no step finished before it (!4880 thread 30678).
      const partial = current && !current.once ? writesBeforeFailure(err) : 0;
      if (current && (doneSteps.current.size > 0 || partial > 0)) {
        const failedAt = steps.indexOf(current);
        setProgress({
          done: [
            ...[...doneSteps.current].map(([key, label]) => ({ key, label })),
            ...(partial > 0 ? [{ key: `${current.key}:partial`, label: partialLabel(current.key, partial) }] : []),
          ],
          failed: { key: current.key, label: current.label },
          todo: steps.slice(failedAt + 1).map((s) => s.label),
        });
      }
      return;
    } finally {
      setSaving(false);
      setConfirmClearOpen(false);
    }

    // The save finished, so the next one starts from nothing: a second save
    // from the same open dialog (the "Updated" confirm dismissed) must not skip
    // the laser writes or the removal confirm (!4880 thread 30531).
    setProgress(null);
    doneSteps.current = new Map();
    confirmedStranded.current = "";
    setCreatedClaimantId(undefined);
    triggerRecomputeRefresh({ notifyUser: false });
    if (mode === "edit") {
      void refetchContractLaser();
      setSavedKind(saveKind);
      // Show confirmation dialog instead of immediately closing
      setEditConfirmOpen(true);
      return;
    }
    notify(
      <Alert severity={AlertType.SUCCESS}>
        {isCreatingNew && !presetClaimant
          ? "Claimant created and lasered successfully"
          : "Claimant lasered successfully"}
      </Alert>
    );
    onClose();
    onSuccess?.();
  }, [
    selectedKind,
    policyId,
    mode,
    existingRecord,
    createdClaimantId,
    presetClaimant,
    selectedClaimant,
    isCreatingNew,
    newFirstName,
    newLastName,
    newMemberId,
    clientId,
    effectiveDate,
    existingStraight,
    amount,
    isContractExcluded,
    existingContract,
    buildContractLaserBody,
    clearPlan,
    conditionDrafts,
    standingSet,
    createTargetPolicyData,
    create,
    update,
    notify,
    notifyError,
    triggerRecomputeRefresh,
    refetchContractLaser,
    onClose,
    onSuccess,
  ]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitAttempted(true);
    const error = blockingError || missingError || contractLaserError;
    if (error) {
      if (contractLaserError) setContractTouched(true);
      notify(error, { type: AlertType.WARNING });
      return;
    }
    // Asked again whenever the warning differs from the one confirmed, retries included.
    if (strandedText && strandedText !== confirmedStranded.current) {
      setConfirmStrandedOpen(true);
      return;
    }
    continueSave();
  };

  /** Past the expected-trigger warning: the type-change confirm if any, then the save. */
  const continueSave = () => {
    setConfirmStrandedOpen(false);
    if (clearText && doneSteps.current.size === 0) {
      setConfirmClearOpen(true);
      return;
    }
    void runSave();
  };

  // ── Rendering helpers ──
  const fieldsLocked = saving;
  const typeLocked = partlySaved || saving;
  const kindOptions = LASER_KIND_OPTIONS.filter((o) => o.value !== "conditional" || !conditionDrafts.unsupported);

  const progressText = progress
    ? (() => {
        const pending = conditionDrafts.pendingWrites;
        const failedLabel =
          progress.failed.key === "conditions" && pending > 0
            ? `${pending} conditional laser${pending === 1 ? "" : "s"}`
            : progress.failed.label;
        return `Not everything was saved. Done: ${joinAnd(progress.done.map((d) => d.label))}. Not done: ${joinAnd([
          failedLabel,
          ...progress.todo,
        ])}. Correct the problem and save again; what is done is not sent again.`;
      })()
    : "";

  // Legacy data saved as Contract keeps its other parts; say they have no effect.
  const contractExtras =
    selectedKind === "contract" && !clearPlan.straight && !clearPlan.conditions
      ? [
          ...(existingStraight
            ? [`a straight laser (${formatUSD(existingStraight.amt_lasered_deductible ?? 0, NO_DECIMALS)})`]
            : []),
          ...(savedConditions.length > 0 ? [conditionListText(savedConditions)] : []),
        ]
      : [];

  const legacyConditions = isLegacyConditionBased ? existingStraight?.conditions : undefined;
  const legacyIcd = Array.isArray(legacyConditions?.icd_codes) ? (legacyConditions.icd_codes as string[]) : [];
  const legacyCpt = Array.isArray(legacyConditions?.cpt_codes) ? (legacyConditions.cpt_codes as string[]) : [];
  const legacyQualifying =
    typeof legacyConditions?.qualifying_condition === "string" ? legacyConditions.qualifying_condition : "";
  const legacyNote = isLegacyConditionBased && (
    <Alert severity="info" variant="outlined" sx={{ fontSize: "0.813rem" }} data-testid="legacy-condition-based">
      {LEGACY_CONDITION_BASED_NOTE}
      <Stack spacing={0.5} sx={{ mt: 1 }}>
        {legacyQualifying && <ReadOnlyLine label="Qualifying condition" value={formatToTitleCase(legacyQualifying)} />}
        {existingStraight?.laser_reason && <ReadOnlyLine label="Reason" value={existingStraight.laser_reason} />}
        {legacyIcd.length > 0 && <ReadOnlyCodes label="ICD codes" codes={legacyIcd} />}
        {legacyCpt.length > 0 && <ReadOnlyCodes label="CPT codes" codes={legacyCpt} />}
      </Stack>
    </Alert>
  );

  const straightFields = (amountLabel: string, required: boolean) => (
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <TextField
        label={amountLabel}
        type="number"
        required={required}
        fullWidth
        disabled={straightLocked || fieldsLocked}
        value={amtLaseredDeductible}
        onChange={(e) => setAmtLaseredDeductible(e.target.value)}
        size="small"
        error={!!amountError || (submitAttempted && required && !standingSet)}
        helperText={amountError || (selectedKind === "conditional" ? "Optional" : undefined)}
        slotProps={{
          input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
        }}
      />
      <TextField
        label="Effective Date"
        type="date"
        fullWidth
        required={required || standingSet}
        disabled={straightLocked || fieldsLocked}
        value={effectiveDate}
        onChange={(e) => setEffectiveDate(e.target.value)}
        size="small"
        error={!!effectiveDateError || (submitAttempted && writesStraight && !effectiveDate)}
        helperText={
          effectiveDateError ||
          (policyEffectiveStart && policyEffectiveEnd
            ? `Policy: ${policyEffectiveStart} to ${policyEffectiveEnd}`
            : undefined)
        }
        slotProps={{
          htmlInput: { min: policyEffectiveStart, max: policyEffectiveEnd },
          inputLabel: { shrink: true },
        }}
      />
    </Stack>
  );

  const contractFieldsDisabled = contractLocked || fieldsLocked;
  const touchContract = () => setContractTouched(true);

  return (
    <>
      <Dialog
        open={open && !editConfirmOpen}
        onClose={(_event, reason) => {
          if (reason !== "backdropClick" && !saving) onClose();
        }}
        fullWidth
        maxWidth="sm"
        scroll="paper"
      >
        <DialogTitle variant="h3" sx={{ borderBottom: 1, borderColor: "divider" }}>
          {mode === "create" ? "Add Lasered Claimant" : "Edit Lasered Claimant"}
        </DialogTitle>

        {/* A flex column filling the paper, so DialogContent scrolls and the
            actions stay in view (scroll="paper" alone cannot reach through the
            form; UX review #12). */}
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0 }}
        >
          <DialogContent dividers sx={{ pb: 4 }}>
            <Stack spacing={3}>
              {progress && (
                <Alert severity="warning" role="alert" data-testid="laser-saved-partly">
                  {progressText}
                </Alert>
              )}
              {/* Preset claimant — create mode bound to the claimant in view. */}
              {mode === "create" && presetClaimant && (
                <TextField
                  label="Claimant"
                  value={claimantDisplayName(presetClaimant)}
                  size="small"
                  disabled
                  fullWidth
                />
              )}

              {/* Claimant selection — create mode, selecting existing */}
              {mode === "create" && !isCreatingNew && !presetClaimant && (
                <Box>
                  <Autocomplete
                    options={availableClaimants}
                    getOptionLabel={claimantDisplayName}
                    value={selectedClaimant}
                    disabled={partlySaved || fieldsLocked}
                    onChange={(_e, newValue) => setSelectedClaimant(newValue)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => <TextField {...params} label="Claimant" required size="small" />}
                  />
                  {!partlySaved && (
                    <Link
                      component="button"
                      type="button"
                      variant="caption"
                      onClick={() => {
                        setIsCreatingNew(true);
                        setSelectedClaimant(null);
                      }}
                      sx={{ mt: 0.5, cursor: "pointer" }}
                    >
                      or create a new claimant
                    </Link>
                  )}
                </Box>
              )}

              {/* Inline new claimant fields — create mode, creating new */}
              {mode === "create" && isCreatingNew && !presetClaimant && (
                <Box>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="First Name"
                      required
                      fullWidth
                      disabled={partlySaved || fieldsLocked}
                      value={newFirstName}
                      onChange={(e) => setNewFirstName(e.target.value)}
                      size="small"
                    />
                    <TextField
                      label="Last Name"
                      required
                      fullWidth
                      disabled={partlySaved || fieldsLocked}
                      value={newLastName}
                      onChange={(e) => setNewLastName(e.target.value)}
                      size="small"
                    />
                  </Stack>
                  <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                    <TextField
                      label="Member ID"
                      fullWidth
                      disabled={partlySaved || fieldsLocked}
                      value={newMemberId}
                      onChange={(e) => setNewMemberId(e.target.value)}
                      size="small"
                      helperText="Optional"
                    />
                  </Stack>
                  {!partlySaved && (
                    <Link
                      component="button"
                      type="button"
                      variant="caption"
                      onClick={() => {
                        setIsCreatingNew(false);
                        setNewFirstName("");
                        setNewLastName("");
                        setNewMemberId("");
                      }}
                      sx={{ mt: 0.5, cursor: "pointer" }}
                    >
                      or select an existing claimant
                    </Link>
                  )}
                </Box>
              )}

              {/* Edit mode: show claimant name as read-only */}
              {mode === "edit" && existingRecord && (
                <TextField
                  label="Claimant"
                  value={
                    [existingRecord.claimant_first_name, existingRecord.claimant_last_name].filter(Boolean).join(" ") ||
                    existingRecord.claimant_name ||
                    ""
                  }
                  size="small"
                  disabled
                  fullWidth
                />
              )}

              {/* Laser type — radio cards */}
              <Box>
                <Typography
                  id="laser-type-label"
                  variant="body2"
                  fontWeight={500}
                  sx={{ mb: 1 }}
                  color={submitAttempted && !selectedKind ? "error" : undefined}
                >
                  Laser Type *
                </Typography>
                <RadioGroup
                  aria-labelledby="laser-type-label"
                  value={selectedKind}
                  onChange={(e) => {
                    setKind(e.target.value as LaserKind);
                    setKindTouched(true);
                  }}
                >
                  <Stack spacing={1.5}>
                    {kindOptions.map((option) => {
                      const isSelected = selectedKind === option.value;
                      return (
                        <Box
                          key={option.value}
                          onClick={() => {
                            if (typeLocked) return;
                            setKind(option.value);
                            setKindTouched(true);
                          }}
                          data-testid={`laser-kind-${option.value}`}
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 0.5,
                            p: 1.5,
                            borderRadius: 1.5,
                            cursor: typeLocked ? "default" : "pointer",
                            opacity: typeLocked && !isSelected ? 0.6 : 1,
                            border: isSelected
                              ? `2px solid ${theme.palette.primary.main}`
                              : `1px solid ${theme.palette.divider}`,
                            bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.04) : "transparent",
                            transition: "all 0.15s ease",
                            "&:hover": {
                              borderColor: isSelected ? theme.palette.primary.main : theme.palette.action.hover,
                              bgcolor: isSelected
                                ? alpha(theme.palette.primary.main, 0.06)
                                : alpha(theme.palette.action.hover, 0.04),
                            },
                          }}
                        >
                          <Radio
                            value={option.value}
                            checked={isSelected}
                            disabled={typeLocked}
                            size="small"
                            sx={{ p: 0, mt: 0.25 }}
                            slotProps={{ input: { "aria-label": option.title } }}
                          />
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {option.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                              {option.description}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </RadioGroup>
              </Box>

              {/* ── Standard: the lasered deductible and its effective date ── */}
              {selectedKind === "standard" && (
                <>
                  {legacyNote}
                  {straightFields("Lasered Specific Deductible", true)}
                </>
              )}

              {/* ── Conditional: optional standing deductible, then the conditional lasers ── */}
              {selectedKind === "conditional" && (
                <>
                  <Box data-testid="standing-deductible">
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                      {STANDING_DEDUCTIBLE_HINT}
                    </Typography>
                    <Stack spacing={1.5}>
                      {legacyNote}
                      {straightFields("Lasered Specific Deductible", false)}
                    </Stack>
                  </Box>
                  {/* Its own titled section (FormSection), with its own trailing divider. */}
                  <LaseredConditionsEditor
                    drafts={conditionDrafts}
                    defaults={conditionWindowDefaults}
                    disabledReason={conditionsDisabledReason}
                    inertWarning={conditionsInertWarning}
                    locked={fieldsLocked || conditionDrafts.isPersisting}
                    expectedTrigger={expectedTriggerOptions}
                  />
                  {submitAttempted && conditionDrafts.drafts.length === 0 && !conditionsDisabledReason && (
                    <Typography variant="caption" color="error" role="alert" sx={{ mt: -2 }}>
                      Add at least one conditional laser.
                    </Typography>
                  )}
                </>
              )}

              {/* ── Contract laser / full exclusion (Feature #5287) ── */}
              {selectedKind === "contract" && (
                <Box data-testid="contract-laser-fields">
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                    Contract Laser
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                    An elevated specific deductible scoped to a contract period. Only claims incurred AND paid inside
                    both windows whose CPT is not excluded count toward it.
                  </Typography>

                  {contractExtras.length > 0 && (
                    <Alert
                      severity="info"
                      variant="outlined"
                      sx={{ mb: 1.5, fontSize: "0.813rem" }}
                      data-testid="contract-legacy-extras"
                    >
                      This claimant also has {joinAnd(contractExtras)} on this policy. Contract terms take precedence:
                      {savedConditions.length > 0 ? " its conditional lasers are inert" : " they have no effect"} while
                      the contract {isContractExcluded ? "exclusion" : "laser"} is set.
                    </Alert>
                  )}

                  <FormControlLabel
                    control={
                      <Switch
                        checked={isContractExcluded}
                        disabled={contractFieldsDisabled}
                        onChange={(e) => {
                          setIsContractExcluded(e.target.checked);
                          touchContract();
                        }}
                        size="small"
                      />
                    }
                    label="Fully exclude this claimant from the contract"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                    When on, the claimant is carved out of the contract entirely (no specific/aggregate eligibility);
                    the contract-laser inputs below are disabled and the two modes are mutually exclusive.
                  </Typography>

                  <Stack spacing={2}>
                    <Stack direction="row" spacing={2}>
                      <TextField
                        label="Incurred Start"
                        type="date"
                        fullWidth
                        disabled={isContractExcluded || contractFieldsDisabled}
                        value={contractIncurredStart}
                        onChange={(e) => {
                          setContractIncurredStart(e.target.value);
                          touchContract();
                        }}
                        size="small"
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                      <TextField
                        label="Incurred End"
                        type="date"
                        fullWidth
                        disabled={isContractExcluded || contractFieldsDisabled}
                        value={contractIncurredEnd}
                        onChange={(e) => {
                          setContractIncurredEnd(e.target.value);
                          touchContract();
                        }}
                        size="small"
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                    </Stack>
                    <Stack direction="row" spacing={2}>
                      <TextField
                        label="Paid Start"
                        type="date"
                        fullWidth
                        disabled={isContractExcluded || contractFieldsDisabled}
                        value={contractPaidStart}
                        onChange={(e) => {
                          setContractPaidStart(e.target.value);
                          touchContract();
                        }}
                        size="small"
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                      <TextField
                        label="Paid End"
                        type="date"
                        fullWidth
                        disabled={isContractExcluded || contractFieldsDisabled}
                        value={contractPaidEnd}
                        onChange={(e) => {
                          setContractPaidEnd(e.target.value);
                          touchContract();
                        }}
                        size="small"
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                    </Stack>
                    <AutocompleteCodeInput
                      codeType="cpt"
                      value={contractExcludedCpts}
                      onChange={(codes) => {
                        setContractExcludedCpts(codes);
                        touchContract();
                      }}
                      disabled={isContractExcluded || contractFieldsDisabled}
                      label="Excluded Procedures (CPT)"
                      placeholder="Search by code or description (e.g., office visit)"
                    />
                    <TextField
                      label="Contract-Lasered Deductible"
                      type="number"
                      fullWidth
                      required={!isContractExcluded}
                      disabled={isContractExcluded || contractFieldsDisabled}
                      value={contractDeductible}
                      onChange={(e) => {
                        setContractDeductible(e.target.value);
                        touchContract();
                      }}
                      size="small"
                      error={showContractError}
                      helperText={showContractError ? contractLaserError : undefined}
                      slotProps={{
                        input: {
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        },
                      }}
                    />
                  </Stack>
                </Box>
              )}
            </Stack>
          </DialogContent>

          <DialogActions sx={{ borderTop: 1, borderColor: "divider", px: 3, py: 2 }}>
            <Box>
              <Button
                variant={ButtonStyleType.Outlined}
                size={UIComponentSize.Medium}
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </Button>
            </Box>
            <Box>
              <Button
                variant={ButtonStyleType.Contained}
                size={UIComponentSize.Medium}
                type="submit"
                disabled={!!blockingError || !selectedKind || saving || conditionDrafts.isPersisting}
              >
                {progress || createdClaimantId ? "Save again" : mode === "create" ? "Add Claimant" : "Save Changes"}
              </Button>
            </Box>
          </DialogActions>
        </Box>
      </Dialog>

      {/* A save whose new terms leave an expected trigger uncovered asks first (#7361). */}
      <ConfirmationDialog
        open={confirmStrandedOpen}
        title={
          stranded.some((r) => r.reapplies)
            ? `Save terms that change how the expected trigger${stranded.length > 1 ? "s apply" : " applies"}?`
            : `Save terms that no longer cover the expected trigger${stranded.length > 1 ? "s" : ""}?`
        }
        message={strandedText}
        confirmLabel={ConfirmLabelType.SaveAnyway}
        cancelLabel={CancelLabelType.Back}
        confirmColor={ColorType.Warning}
        onCancel={() => setConfirmStrandedOpen(false)}
        onConfirm={() => {
          confirmedStranded.current = strandedText;
          continueSave();
        }}
      />

      {/* A save that removes the old type's data asks first, listing it. */}
      <ConfirmationDialog
        open={confirmClearOpen}
        title={
          selectedKind && selectedKind !== originKind
            ? `Change to ${LASER_KIND_LABELS[selectedKind]}?`
            : "Remove the straight laser?"
        }
        message={`${clearText} Stop-loss claims are recalculated.`}
        confirmLabel={ConfirmLabelType.Continue}
        confirmColor={ColorType.Error}
        onCancel={() => setConfirmClearOpen(false)}
        onConfirm={() => void runSave()}
        isLoading={saving}
      />

      {/* Edit confirmation dialog */}
      <Dialog open={editConfirmOpen} onClose={() => setEditConfirmOpen(false)}>
        <DialogTitle>
          {savedKind === "conditional"
            ? "Conditional Lasers Updated"
            : savedKind === "contract"
              ? "Contract Laser Updated"
              : "Lasered Deductible Updated"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {savedKind === "conditional"
              ? "The conditional lasers have been saved."
              : savedKind === "contract"
                ? isContractExcluded
                  ? "The contract exclusion has been saved."
                  : "The contract laser has been saved."
                : "The lasered deductible has been updated."}{" "}
            If this change affects claims processing or requires follow-up, create a task so it doesn&apos;t get missed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton
            variant="contained"
            onClick={() => {
              setEditConfirmOpen(false);
              onClose();
              onSuccess?.();
            }}
          >
            Got it
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

const ReadOnlyLine = ({ label, value }: { label: string; value: string }) => (
  <Typography variant="caption" component="div">
    <Box component="span" sx={{ color: "text.secondary" }}>
      {label}:
    </Box>{" "}
    {value}
  </Typography>
);

const ReadOnlyCodes = ({ label, codes }: { label: string; codes: string[] }) => (
  <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
    <Typography variant="caption" color="text.secondary">
      {label}:
    </Typography>
    {codes.map((code) => (
      <Chip key={code} label={code} size="small" sx={{ fontFamily: "monospace", fontSize: "0.75rem" }} />
    ))}
  </Stack>
);
